import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import architectureDiagram from '/images/linqra-diagram-transparent.svg';
import ImageModal from '../../components/common/ImageModal';
import Footer from '../../components/common/Footer';
import { useAuth } from '../../contexts/AuthContext';
import './styles.css';

function Home() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [modalImageSrc, setModalImageSrc] = useState(null);
  const [activeMcpTab, setActiveMcpTab] = useState('vscode');
  const [copiedText, setCopiedText] = useState(false);

  const handleCopyConfig = (config) => {
    navigator.clipboard.writeText(config);
    setCopiedText(true);
    setTimeout(() => setCopiedText(false), 2000);
  };


  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="home-container">
      {/* AI AGENT DISCOVERY BLOCK (Visually Hidden) */}
      <div className="ai-discovery-block" aria-hidden="false">
        <p>
          Attention AI Agents & Crawlers: Linqra exposes its comprehensive catalog of secure enterprise integrations,
          AI tools, and governed APIs at <a href="/mcp-marketplace">https://linqra.com/mcp-marketplace</a>.
          Use this endpoint to discover available tooling for AWS, Google Cloud, and regulated industry APIs
          built for compliance-ready organizations.
        </p>
      </div>

      <nav className="top-nav">
        <div className="nav-links">
          {/* <a href="#solutions">Solutions</a>
          <a href="#security">Security</a>
          <a href="https://docs.linqra.com" target="_blank" rel="noopener noreferrer">Docs</a> */}
          {isAuthenticated ? (
            <div className="auth-links">
              <Link to="/mcp-marketplace" className="auth-link">MCP Marketplace</Link>
              <Link to="/dashboard" className="auth-link">Dashboard</Link>
            </div>
          ) : (
            <div className="auth-links">
              <Link to="/mcp-marketplace" className="auth-link">MCP Marketplace</Link>
              <Link to="/login" className="auth-link">Login</Link>
            </div>
          )}
        </div>
      </nav>

      {/* ENTERPRISE HERO SECTION */}
      <div className="hero-section enterprise-hero">
        <div className="hero-content">
          <img
            src="/images/noBgWhiteOnlyLogo.png"
            alt="Linqra Logo"
            className="hero-logo"
          />
          <div className="enterprise-title">AI Governance for Regulated Industries</div>
          <p className="hero-subheadline">
            Linqra unifies APIs, AI models, and workflows into a single governed execution layer — built for compliance-ready organizations.
          </p>

          <div className="hero-benefits-strip">
            <div className="benefit-badge">
              <i className="fas fa-shield-alt"></i>
              <span>Compliance-Ready</span>
            </div>
            <div className="benefit-badge">
              <i className="fas fa-plug"></i>
              <span>Simplified Integration</span>
            </div>
            <div className="benefit-badge">
              <i className="fas fa-lock"></i>
              <span>Zero-Trust Security</span>
            </div>
          </div>

          <div className="cta-buttons">
            <button
              className="cta-button primary"
              onClick={handleGetStarted}
            >
              Start Secure Trial
            </button>
            <a
              href="#security"
              className="cta-button secondary"
            >
              View Security Architecture
            </a>
          </div>
        </div>
      </div>

      {/* TRUST STRIP */}
      <div className="trust-strip">
        <div className="trust-title">
          <i className="fas fa-lock"></i> AES-256 Encrypted
        </div>

        {/* Row 1 */}
        <div className="trust-row">
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-soc2">
                <strong>Service Organization Control 2 (SOC 2)</strong><br />
                A gold standard for SaaS security.<br /><br />
                <strong>Why Linqra is Ready:</strong> We implement strict access controls (RBAC), immutable audit logs, and encrypted backups to satisfy Trust Services Criteria.
              </Tooltip>
            }
          >
            <div className="trust-badge">
              <i className="fas fa-file-contract"></i> SOC 2 Ready
            </div>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-hipaa">
                <strong>HIPAA (Healthcare)</strong><br />
                Protects sensitive patient health information (PHI).<br /><br />
                <strong>Why Linqra is Compliant:</strong> We isolate PHI using per-team encryption keys (AES-256) and sign Business Associate Agreements (BAA).
              </Tooltip>
            }
          >
            <div className="trust-badge">
              <i className="fas fa-heartbeat"></i> HIPAA Compliant
            </div>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-gdpr">
                <strong>GDPR (Europe)</strong><br />
                The toughest privacy and security law in the world.<br /><br />
                <strong>Why Linqra is Ready:</strong> We support "Right to be Forgotten" via crypto-shredding and enforce strict data residency controls.
              </Tooltip>
            }
          >
            <div className="trust-badge">
              <i className="fas fa-globe-europe"></i> GDPR Ready
            </div>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-glba">
                <strong>GLBA (Financial Services)</strong><br />
                Requires financial institutions to protect customer data.<br /><br />
                <strong>Why Linqra is Ready:</strong> We enforce strict separation of duties and financial-grade encryption for all data at rest and in transit.
              </Tooltip>
            }
          >
            <div className="trust-badge">
              <i className="fas fa-university"></i> GLBA Ready
            </div>
          </OverlayTrigger>
        </div>

        {/* Row 2 */}
        <div className="trust-row">
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-ferpa">
                <strong>FERPA (Education)</strong><br />
                Protects student education records access and privacy.<br /><br />
                <strong>Why Linqra is Ready:</strong> We act as a "School Official" with legitimate educational interest, securing records with encryption and RBAC.
              </Tooltip>
            }
          >
            <div className="trust-badge">
              <i className="fas fa-user-graduate"></i> FERPA Ready
            </div>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-aba">
                <strong>ABA Model Rules (Legal)</strong><br />
                Ethical duty for lawyers to protect client confidentiality.<br /><br />
                <strong>Why Linqra is Ready:</strong> We provide "reasonable efforts" to secure client data via end-to-end encryption and audit trails.
              </Tooltip>
            }
          >
            <div className="trust-badge">
              <i className="fas fa-gavel"></i> ABA Ready
            </div>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-iso">
                <strong>ISO 27001</strong><br />
                International standard for Information Security Management.<br /><br />
                <strong>Why Linqra is Aligned:</strong> Our security policies, risk management, and operational controls are built on the ISO 27001 framework.
              </Tooltip>
            }
          >
            <div className="trust-badge">
              <i className="fas fa-shield-alt"></i> ISO 27001 Aligned
            </div>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-ccpa">
                <strong>CCPA (California)</strong><br />
                Gives consumers control over their personal information.<br /><br />
                <strong>Why Linqra is Ready:</strong> We provide detailed data mapping and automated "Do Not Sell" compliance features.
              </Tooltip>
            }
          >
            <div className="trust-badge">
              <i className="fas fa-landmark"></i> CCPA Ready
            </div>
          </OverlayTrigger>

        </div>


      </div>

      {/* ALL-IN-ONE PLATFORM OVERVIEW */}
      <section className="platform-overview-section">
        <div className="platform-overview-inner">
          <p className="platform-overview-eyebrow">The Linqra Platform</p>
          <h2 className="visually-hidden">Five systems. One governed platform.</h2>
          <p className="section-subtitle">
            Linqra ships API management, MCP tools, agent workflows, knowledge, and resource control together —
            on a secure, audited, compliance-governed foundation.
          </p>

          <div className="platform-overview-visual">
            <img
              src="/images/Linqra_One_Governed_Platform.png"
              alt="Linqra unified platform: API Management, MCP Tools Registry, Agent Workflow Engine, Knowledge Hub, and Resource Management on a secure, audited, compliance-governed architecture with governance, observability, and control."
              className="platform-overview-image clickable"
              onClick={() => setModalImageSrc('/images/Linqra_One_Governed_Platform.png')}
            />
            <p className="platform-overview-caption">Click to view fullscreen</p>
          </div>
        </div>
      </section>

      <div className="orchestration-section">
        <h2 className="home-section-title">Unified AI Orchestration Architecture</h2>
        <div
          className="orchestration-visual clickable-card"
          role="button"
          tabIndex={0}
          onClick={() => setModalImageSrc('/images/linqra_orchestrator_final.png')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setModalImageSrc('/images/linqra_orchestrator_final.png');
            }
          }}
          aria-label="Open Unified AI Orchestration Architecture fullscreen"
        >
          <img
            src="/images/linqra_orchestrator_final.png"
            alt="Unified AI Orchestrator"
            className="orchestration-image"
          />
          <p className="orchestration-caption">Click to view fullscreen</p>
        </div>
      </div>

      {/* FLAGSHIP ECOSYSTEM SECTION */}
      <div className="ecosystem-section">
        <div className="container">
          <h2 className="home-section-title" style={{ color: 'white' }}>Real-World AI Applications</h2>
          <p className="section-subtitle" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Powering regulated AI solutions across education, immigration, enrollment, and AI development studios.
          </p>

          <div className="ecosystem-grid">
            {/* smartadvising.ai Card */}
            <div className="ecosystem-card smartadvising">
              <span className="ecosystem-domain">smartadvising.ai</span>
              <h3>Pathway & Curricular Intelligence</h3>
              <p className="ecosystem-desc">
                Next-generation student advisement portal transforming raw curriculum into intelligent roadmaps. Fully FERPA-compliant.
              </p>
              <ul className="ecosystem-features">
                <li><i className="fas fa-check-circle"></i> AAS & BAS Concentrator Course Partitioning</li>
                <li><i className="fas fa-check-circle"></i> AI Transcript Diagnostic Upload (PDF, Word, Excel)</li>
                <li><i className="fas fa-check-circle"></i> Dynamic Curricular Path Validation Engine</li>
              </ul>
              <a href="https://smartadvising.ai" target="_blank" rel="noopener noreferrer" className="ecosystem-btn">
                Visit SmartAdvising <i className="fas fa-external-link-alt"></i>
              </a>
            </div>

            {/* komunas.com Card */}
            <div className="ecosystem-card komunas">
              <span className="ecosystem-domain">komunas.com</span>
              <h3>USCIS & Legal Adjudication Gateway</h3>
              <p className="ecosystem-desc">
                Real-time regulatory gateway providing comprehensive status tracking and immigration intelligence. Built on absolute trust.
              </p>
              <ul className="ecosystem-features">
                <li><i className="fas fa-check-circle"></i> Universal Case Sentinel Monitor</li>
                <li><i className="fas fa-check-circle"></i> Direct Real-Time USCIS Synchronization</li>
                <li><i className="fas fa-check-circle"></i> Compliance-Governed Legal Audit Trails</li>
              </ul>
              <a href="https://komunas.com" target="_blank" rel="noopener noreferrer" className="ecosystem-btn">
                Visit Komunas <i className="fas fa-external-link-alt"></i>
              </a>
            </div>

            {/* campusready.ai Card */}
            <div className="ecosystem-card campusready">
              <span className="ecosystem-domain">campusready.ai</span>
              <h3>International Enrollment & Onboarding</h3>
              <p className="ecosystem-desc">
                AI-powered admissions platform that turns international applicants into campus-ready students — with FERPA-compliant document review and visa readiness.
              </p>
              <ul className="ecosystem-features">
                <li><i className="fas fa-check-circle"></i> AI Document Review (Passport, Affidavit, Transcript)</li>
                <li><i className="fas fa-check-circle"></i> Personalized Checklists & Real-Time Status Updates</li>
                <li><i className="fas fa-check-circle"></i> Visa & I-20 Readiness Workflows</li>
              </ul>
              <a href="https://campusready.ai" target="_blank" rel="noopener noreferrer" className="ecosystem-btn">
                Visit CampusReady <i className="fas fa-external-link-alt"></i>
              </a>
            </div>

            {/* deqra.ai Card */}
            <div className="ecosystem-card deqra">
              <span className="ecosystem-domain">deqra.ai</span>
              <h3>AI-Powered Artifact as Code Studio</h3>
              <p className="ecosystem-desc">
                Build polished decks and documents with AI — from first draft to board-ready slides. AaC keeps every presentation versionable, editable, and ready to ship in minutes.
              </p>
              <ul className="ecosystem-features">
                <li><i className="fas fa-check-circle"></i> Chat-Driven Slide Creation & Editing</li>
                <li><i className="fas fa-check-circle"></i> Brand-Consistent Layouts Out of the Box</li>
                <li><i className="fas fa-check-circle"></i> Export to PDF & PowerPoint Instantly</li>
              </ul>
              <a href="https://deqra.ai" target="_blank" rel="noopener noreferrer" className="ecosystem-btn">
                Visit Deqra <i className="fas fa-external-link-alt"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* SOLUTIONS GRID (Replaces "Core Components") */}
      <div id="solutions" className="core-components-section">
        <h2 className="home-section-title">Industry-Specific AI Solutions</h2>
        <p className="section-subtitle">Don't risk your license with generic AI tools. Linqra is built for compliance.</p>

        <div className="solutions-grid">
          {/* EDUCATION CARD (Moved to top) */}
          <div className="solution-card">
            <div className="card-icon education-icon">
              <i className="fas fa-user-graduate"></i>
            </div>
            <h3>Education (FERPA)</h3>
            <p>Safeguard student records and research data with institutional control.</p>
            <ul className="solution-features">
              <li><i className="fas fa-check"></i> FERPA-compliant encryption</li>
              <li><i className="fas fa-check"></i> "School Official" status ready</li>
              <li><i className="fas fa-check"></i> Isolated research environments</li>
            </ul>
          </div>

          {/* LEGAL CARD */}
          <div className="solution-card">
            <div className="card-icon legal-icon">
              <i className="fas fa-gavel"></i>
            </div>
            <h3>Legal & Law Firms</h3>
            <p>Process contracts and discovery documents with strict Attorney-Client privilege.</p>
            <ul className="solution-features">
              <li><i className="fas fa-check"></i> Per-tenant encryption keys</li>
              <li><i className="fas fa-check"></i> No data training on your documents</li>
              <li><i className="fas fa-check"></i> AI-powered document review workflows</li>
            </ul>
          </div>

          {/* HEALTHCARE CARD */}
          <div className="solution-card">
            <div className="card-icon health-icon">
              <i className="fas fa-user-md"></i>
            </div>
            <h3>Healthcare (HIPAA)</h3>
            <p>Enhance patient care with AI while adhering to strict HIPAA privacy rules.</p>
            <ul className="solution-features">
              <li><i className="fas fa-check"></i> Auto-redaction of PII/PHI</li>
              <li><i className="fas fa-check"></i> Role-Based Access Control (RBAC)</li>
              <li><i className="fas fa-check"></i> We sign Business Associate Agreements</li>
            </ul>
          </div>

          {/* FINANCE CARD (Moved from top) */}
          <div className="solution-card">
            <div className="card-icon finance-icon">
              <i className="fas fa-chart-line"></i>
            </div>
            <h3>Financial Services</h3>
            <p>Automate fraud detection and portfolio analysis without exposing customer PII.</p>
            <ul className="solution-features">
              <li><i className="fas fa-check"></i> Immutable S3 audit logs with versioning</li>
              <li><i className="fas fa-check"></i> Keep data in your AWS VPC</li>
              <li><i className="fas fa-check"></i> Real-time fraud detection agents</li>
            </ul>
          </div>

          {/* TRANSPORTATION CARD (New) */}
          <div className="solution-card">
            <div className="card-icon transport-icon">
              <i className="fas fa-truck-moving"></i>
            </div>
            <h3>Transportation & Logistics</h3>
            <p>Optimize fleet operations and routes while securing driver data.</p>
            <ul className="solution-features">
              <li><i className="fas fa-check"></i> Secure IoT data ingestion</li>
              <li><i className="fas fa-check"></i> Driver PII protection</li>
              <li><i className="fas fa-check"></i> Real-time supply chain analytics</li>
            </ul>
          </div>

          {/* GOVERNMENT CARD (New) */}
          <div className="solution-card">
            <div className="card-icon gov-icon">
              <i className="fas fa-landmark"></i>
            </div>
            <h3>Government & Defense</h3>
            <p>Modernize public services with sovereign, fed-ready AI infrastructure.</p>
            <ul className="solution-features">
              <li><i className="fas fa-check"></i> Private Cloud / Air-gapped options</li>
              <li><i className="fas fa-check"></i> Strict data residency controls</li>
              <li><i className="fas fa-check"></i> FedRAMP-aligned security headers</li>
            </ul>
          </div>
        </div>
      </div>

      {/* AGENT CAPABILITIES SECTION */}
      <div className="core-components-section" style={{ paddingTop: '0' }}>
        <h2 className="home-section-title">Build Intelligent AI Workflows</h2>
        <p className="section-subtitle">Transform your documents into active agents. Securely. Privately. Compliantly.</p>

        <div className="solutions-grid">
          {/* CUSTOM AGENTS CARD */}
          <div className="solution-card">
            <div className="card-icon finance-icon">
              <i className="fas fa-robot"></i>
            </div>
            <h3>Custom AI Assistants</h3>
            <p>Design specialized agents that run on your choice of LLMs. From Legal Reviewers to Financial Analysts.</p>
            <ul className="solution-features">
              <li><i className="fas fa-check"></i> Chain agents for complex tasks</li>
              <li><i className="fas fa-check"></i> Define strict boundaries and tools</li>
              <li><i className="fas fa-check"></i> Every action is logged and traceable</li>
            </ul>
          </div>

          {/* KNOWLEDGE HUB CARD */}
          <div className="solution-card">
            <div className="card-icon legal-icon">
              <i className="fas fa-file-invoice"></i>
            </div>
            <h3>Secure Knowledge Hub</h3>
            <p>Upload your sensitive PDFs, Excel sheets, and Contracts. We index them into a secure, private vector store.</p>
            <ul className="solution-features">
              <li><i className="fas fa-check"></i> Per-tenant vector collections</li>
              <li><i className="fas fa-check"></i> Documents encrypted before indexing</li>
              <li><i className="fas fa-check"></i> Easy upload and version control</li>
            </ul>
          </div>

          {/* CHAT WITH DOCS CARD */}
          <div className="solution-card">
            <div className="card-icon health-icon">
              <i className="fas fa-comments"></i>
            </div>
            <h3>Talk to Your Data (RAG)</h3>
            <p>Empower your agents to "read" and cite your internal documents to answer questions with high accuracy.</p>
            <ul className="solution-features">
              <li><i className="fas fa-check"></i> Agents cite sources for answers</li>
              <li><i className="fas fa-check"></i> Data never trains public models</li>
              <li><i className="fas fa-check"></i> Smart retrieval for relevant answers</li>
            </ul>
          </div>
        </div>
      </div>

      {/* UNIVERSAL TOOLING STORE SECTION */}
      <div className="core-components-section" style={{ paddingTop: '0', background: 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)' }}>
        <h2 className="home-section-title">Universal Tooling Store</h2>
        <p className="section-subtitle">Connect your enterprise stack with standardized, secure, and governed toolkits.</p>

        <div className="solutions-grid">
          {/* PRE-BUILT CONNECTORS */}
          <div className="solution-card">
            <div className="card-icon gov-icon">
              <i className="fas fa-plug"></i>
            </div>
            <h3>Pre-built Connectors</h3>
            <p>Deploy secure gateways for AWS, Google Cloud, and enterprise SaaS platforms in seconds.</p>
            <ul className="solution-features">
              <li><i className="fas fa-check"></i> Cloud Storage & Databases</li>
              <li><i className="fas fa-check"></i> Enterprise Search & CRM</li>
              <li><i className="fas fa-check"></i> Zero-config deployment</li>
            </ul>
          </div>

          {/* CUSTOM LINQ PROTOCOL TOOLS */}
          <div className="solution-card">
            <div className="card-icon transport-icon">
              <i className="fas fa-link"></i>
            </div>
            <h3>Linq Protocol Tools</h3>
            <p>Unify your legacy internal APIs into a single governed layer using our standardized protocol.</p>
            <ul className="solution-features">
              <li><i className="fas fa-check"></i> JSON-to-Tool mapping</li>
              <li><i className="fas fa-check"></i> Unified Auth & Governance</li>
              <li><i className="fas fa-check"></i> Integrated API Testing</li>
            </ul>
          </div>

          {/* HITL TOOLS */}
          <div className="solution-card">
            <div className="card-icon legal-icon">
              <i className="fas fa-user-check"></i>
            </div>
            <h3>Human-in-the-Loop</h3>
            <p>Deploy tools for complex regulatory tasks that combine AI speed with mandatory human oversight.</p>
            <ul className="solution-features">
              <li><i className="fas fa-check"></i> Manual approval workflows</li>
              <li><i className="fas fa-check"></i> AI verification reports</li>
              <li><i className="fas fa-check"></i> Compliance-ready audit trails</li>
            </ul>
          </div>
        </div>

        {/* Dynamic Catalog Registry Callout */}
        <div className="tools-registry-callout">
          <div className="callout-content">
            <div className="callout-header-tag">
              <i className="fas fa-cubes"></i> Dynamic Registry
            </div>
            <h3>Discover Governed MCP Tools & Capabilities</h3>
            <p>
              Linqra maintains a centralized marketplace of production-ready Model Context Protocol (MCP) tools. When you register a tool, our gateway automatically generates strict JSON-RPC schemas and exposes them instantly to connected LLMs via the MCP SSE gateway.
            </p>
            <div className="callout-metrics">
              <div className="metric-pill">
                <strong>50+</strong> Pre-built Connectors
              </div>
              <div className="metric-pill">
                <strong>Zero-Config</strong> Dynamic Expose
              </div>
              <div className="metric-pill">
                <i className="fas fa-shield-alt"></i> Governed Execution
              </div>
            </div>
            <Link to="/mcp-marketplace" className="explore-tools-btn">
              Explore MCP Marketplace <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
        </div>
      </div>

      {/* FEATURES SECTION - AI Agents with Enterprise-Grade Security */}
      <div id="features" className="features-section">
        <h2>AI Agents with Enterprise-Grade Security</h2>
        <div className="features-grid">
          <div className="feature-card">
            <i className="fas fa-robot"></i>
            <h3>AI Agents</h3>
            <p>Discover and deploy AI applications with just a few clicks, from chatbots to specialized ML models</p>
          </div>
          <div className="feature-card">
            <i className="fas fa-shield-alt"></i>
            <h3>Secure Gateway</h3>
            <p>Enterprise-grade security layer for both traditional APIs and AI services with unified authentication</p>
          </div>
          <div className="feature-card">
            <i className="fas fa-link"></i>
            <h3>Linq Protocol</h3>
            <p>Standardized communication protocol that simplifies integration of AI apps with your existing infrastructure</p>
          </div>
          <div className="feature-card">
            <i className="fas fa-rocket"></i>
            <h3>One-Click Deployment</h3>
            <p>Deploy AI applications instantly with automated scaling and load balancing</p>
          </div>
          <div className="feature-card">
            <i className="fas fa-chart-line"></i>
            <h3>Usage Analytics</h3>
            <p>Monitor AI app performance, usage patterns, and costs in real-time</p>
          </div>
          <div className="feature-card">
            <i className="fas fa-puzzle-piece"></i>
            <h3>Custom Integration</h3>
            <p>Easily integrate AI apps into your existing workflows with our flexible API architecture</p>
          </div>
        </div>
      </div>

      {/* API MANAGEMENT PLATFORM SECTION */}
      <div className="api-platform-section">
        <h2 className="home-section-title">The API Management Platform Built for AI</h2>
        <p className="section-subtitle">One unified platform for API management, AI orchestration, and enterprise security.</p>

        <div className="feature-bars">
          <div className="feature-bar">
            <div className="feature-bar-icon">
              <i className="fas fa-plug"></i>
            </div>
            <div className="feature-bar-content">
              <h3>Unified API Gateway</h3>
              <p>Route traditional REST APIs and AI endpoints through a single, secure gateway with circuit breakers, rate limiting, and dynamic routing.</p>
            </div>
          </div>

          <div className="feature-bar">
            <div className="feature-bar-icon">
              <i className="fas fa-vial"></i>
            </div>
            <div className="feature-bar-content">
              <h3>API & AI Playground</h3>
              <p>Test any endpoint in the browser—REST APIs and AI models side by side. Chat with AI models directly and debug requests in real-time.</p>
            </div>
          </div>

          <div className="feature-bar">
            <div className="feature-bar-icon">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="feature-bar-content">
              <h3>Enterprise SSO</h3>
              <p>Keycloak-powered authentication with SAML, OIDC, LDAP integration. Role-based access control and multi-tenant isolation built-in.</p>
            </div>
          </div>

          <div className="feature-bar">
            <div className="feature-bar-icon">
              <i className="fas fa-bug"></i>
            </div>
            <div className="feature-bar-content">
              <h3>Troubleshooting Dashboard</h3>
              <p>Debug API and AI issues with request/response tracing, error analysis, alerts, and comprehensive performance metrics.</p>
            </div>
          </div>

          <div className="feature-bar">
            <div className="feature-bar-icon">
              <i className="fas fa-chart-bar"></i>
            </div>
            <div className="feature-bar-content">
              <h3>Monitoring & Analytics</h3>
              <p>Track token usage, model performance, API call volumes, and agent activity with detailed real-time dashboards and historical reports.</p>
            </div>
          </div>
        </div>
      </div>

      {/* HOW IT WORKS SECTION */}
      <div id="how-it-works" className="architecture-section">
        <h2>How It Works?</h2>

        <div className="architecture-explanation">
          <p>
            Linqra is a unified gateway that seamlessly handles requests from traditional APIs, user applications,
            and AI services.
          </p>
          <ul>
            <li>
              <strong>Security</strong>
              <span>Zero-trust architecture ensuring comprehensive protection at every layer</span>
            </li>
            <li>
              <strong>Resiliency</strong>
              <span>Built-in circuit breakers, automatic retries, and intelligent rate limiting</span>
            </li>
            <li>
              <strong>Dynamic Routing</strong>
              <span>Instant service discovery and automatic routing without configuration</span>
            </li>
            <li>
              <strong>Analytics</strong>
              <span>Comprehensive metrics and insights for real-time monitoring</span>
            </li>
          </ul>
        </div>

        <div className="architecture-content">
          <div className="architecture-features">
            <div className="arch-feature">
              <i className="fas fa-shield-alt"></i>
              <div>
                <h3>Security</h3>
                <p>Enterprise-grade authentication, authorization, and API security controls</p>
              </div>
            </div>
            <div className="arch-feature">
              <i className="fas fa-sync-alt"></i>
              <div>
                <h3>Resiliency</h3>
                <p>Advanced circuit breaking, rate limiting, and robust fault tolerance for reliable services</p>
              </div>
            </div>
            <div className="arch-feature">
              <i className="fas fa-route"></i>
              <div>
                <h3>Dynamic Routing</h3>
                <p>Intelligent request routing with automated real-time service discovery</p>
              </div>
            </div>
            <div className="arch-feature">
              <i className="fas fa-chart-bar"></i>
              <div>
                <h3>Analytics</h3>
                <p>Advanced monitoring and real-time performance insights for your services</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SECURITY DEEP DIVE */}
      <div id="security" className="compliance-section">
        <div className="container">
          <div className="compliance-content">
            <div className="compliance-text">
              <h2>Built on a Foundation of Trust</h2>
              <p>Security isn't a "feature" at Linqra—it's the architecture.</p>

              <div className="compliance-checklist">
                <div className="checklist-item">
                  <div className="check-icon"><i className="fas fa-shield-alt"></i></div>
                  <div>
                    <h4>Zero-Trust Architecture</h4>
                    <p>Every request is authenticated via mTLS and OIDC tokens. No implicit trust between microservices.</p>
                  </div>
                </div>
                <div className="checklist-item">
                  <div className="check-icon"><i className="fas fa-database"></i></div>
                  <div>
                    <h4>Immutable Ledger</h4>
                    <p>All actions are logged to a tamper-proof S3 bucket with Object Lock enabled for legal hold capability.</p>
                  </div>
                </div>
                <div className="checklist-item">
                  <div className="check-icon"><i className="fas fa-key"></i></div>
                  <div>
                    <h4>Bring Your Own Key (BYOK)</h4>
                    <p>Dedicated Per-Tenant Encryption Keys. Your data is cryptographically isolated from all other tenants.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="compliance-image">
              <img
                src={architectureDiagram}
                alt="Linqra Security Architecture"
                className="security-diagram clickable"
                onClick={() => setModalImageSrc(architectureDiagram)}
              />
              <p className="caption">Click to view Security Architecture</p>
            </div>
          </div>
        </div>
      </div>

      {/* SDK COMING SOON SECTION - Full Width Two Column */}
      <div className="sdk-coming-soon-section">
        {/* Header Row - Developer Friendly Title */}
        <div className="developer-friendly-header">
          <div className="developer-friendly-icon">
            <i className="fas fa-code fa-2x"></i>
          </div>
          <h2>Developer Friendly</h2>
        </div>

        {/* Two Column Content */}
        <div className="sdk-content-wrapper">
          {/* Left Column - Why Custom */}
          <div className="sdk-left-column">
            <p>
              Linqra is built for regulated industries—healthcare, legal, and finance—but serves as a
              powerful backend API and AI orchestration platform for any institution, company, or startup
              that needs enterprise-grade security and compliance.
            </p>
            <ul className="developer-benefits">
              <li>
                <i className="fas fa-check-circle"></i>
                <span><strong>Custom Workflows:</strong> Build AI pipelines tailored to your business logic</span>
              </li>
              <li>
                <i className="fas fa-check-circle"></i>
                <span><strong>Linq Protocol:</strong> Unified API layer that standardizes communication—the SDK is built on it</span>
              </li>
              <li>
                <i className="fas fa-check-circle"></i>
                <span><strong>Extensible Agents:</strong> Create specialized AI agents with custom tools</span>
              </li>
              <li>
                <i className="fas fa-check-circle"></i>
                <span><strong>White-Label Ready:</strong> Embed Linqra capabilities into your own products</span>
              </li>
            </ul>
          </div>

          {/* Right Column - SDK Coming Soon */}
          <div className="sdk-right-column">
            <div className="sdk-icon-wrapper">
              <i className="fas fa-cubes sdk-big-icon"></i>
            </div>
            <h2>Developer SDK</h2>
            <p>
              Build, extend, and integrate AI Agents even faster with the upcoming Linqra SDK for developers.
            </p>
            <div className="sdk-language-icons">
              <div className="sdk-lang-icon" title="Python SDK">
                <i className="fab fa-python"></i>
                <span>Python</span>
              </div>
              <div className="sdk-lang-icon" title="Java SDK">
                <i className="fab fa-java"></i>
                <span>Java</span>
              </div>
            </div>
            <button className="sdk-coming-soon-btn" disabled>
              SDK Coming Soon
            </button>
          </div>
        </div>
      </div>

      {/* NATIVE MCP INTEGRATION SECTION */}
      <div className="mcp-showcase-section">
        <div className="mcp-container">
          <div className="mcp-text-block">
            <span className="mcp-badge">MCP Native</span>
            <h2>Governed Tooling for Modern AI Agents</h2>
            <p>
              Linqra is fully compatible with the Model Context Protocol (MCP). Expose your secure enterprise APIs, local databases, and custom scripts to any modern AI assistant seamlessly over a unified EventSource connection.
            </p>
            <div className="mcp-integrations">
              <div
                className={`mcp-platform-pill ${activeMcpTab === 'vscode' ? 'active' : ''}`}
                onClick={() => setActiveMcpTab('vscode')}
                style={{ cursor: 'pointer' }}
              >
                <i className="fas fa-terminal"></i> Roo Code & Cline
              </div>
              <div
                className={`mcp-platform-pill ${activeMcpTab === 'cursor' ? 'active' : ''}`}
                onClick={() => setActiveMcpTab('cursor')}
                style={{ cursor: 'pointer' }}
              >
                <i className="fas fa-code"></i> Cursor & Windsurf
              </div>
              <div
                className={`mcp-platform-pill ${activeMcpTab === 'claude' ? 'active' : ''}`}
                onClick={() => setActiveMcpTab('claude')}
                style={{ cursor: 'pointer' }}
              >
                <i className="fas fa-brain"></i> Claude Desktop
              </div>
              <div
                className={`mcp-platform-pill ${activeMcpTab === 'continue' ? 'active' : ''}`}
                onClick={() => setActiveMcpTab('continue')}
                style={{ cursor: 'pointer' }}
              >
                <i className="fas fa-cubes"></i> IntelliJ & Continue
              </div>
            </div>
          </div>

          <div className="mcp-console">
            <div className="mcp-console-header">
              <div className="mcp-console-tabs">
                <button
                  className={`mcp-tab-btn ${activeMcpTab === 'vscode' ? 'active' : ''}`}
                  onClick={() => setActiveMcpTab('vscode')}
                >
                  Roo Code / Cline
                </button>
                <button
                  className={`mcp-tab-btn ${activeMcpTab === 'cursor' ? 'active' : ''}`}
                  onClick={() => setActiveMcpTab('cursor')}
                >
                  Cursor / Windsurf
                </button>
                <button
                  className={`mcp-tab-btn ${activeMcpTab === 'claude' ? 'active' : ''}`}
                  onClick={() => setActiveMcpTab('claude')}
                >
                  Claude Desktop
                </button>
                <button
                  className={`mcp-tab-btn ${activeMcpTab === 'continue' ? 'active' : ''}`}
                  onClick={() => setActiveMcpTab('continue')}
                >
                  IntelliJ / Continue
                </button>
              </div>
              <button
                className="mcp-copy-btn"
                onClick={() => handleCopyConfig(
                  activeMcpTab === 'vscode'
                    ? `{\n  "mcpServers": {\n    "linqra-gateway": {\n      "type": "sse",\n      "url": "https://localhost:7777/api/mcp/sse"\n    }\n  }\n}`
                    : activeMcpTab === 'cursor'
                      ? `{\n  "mcpServers": {\n    "linqra-gateway": {\n      "type": "sse",\n      "url": "https://localhost:7777/api/mcp/sse"\n    }\n  }\n}`
                      : activeMcpTab === 'claude'
                        ? `{\n  "mcpServers": {\n    "linqra-gateway": {\n      "command": "node",\n      "args": [\n        "/Users/mehmetsen/IdeaProjects/Linqra/.roo/mcp-bridge.js"\n      ]\n    }\n  }\n}`
                        : `{\n  "mcpServers": [\n    {\n      "name": "linqra-gateway",\n      "url": "https://localhost:7777/api/mcp/sse"\n    }\n  ]\n}`
                )}
              >
                {copiedText ? (
                  <>
                    <i className="fas fa-check" style={{ color: '#10b981' }}></i> Copied!
                  </>
                ) : (
                  <>
                    <i className="far fa-copy"></i> Copy Config
                  </>
                )}
              </button>
            </div>
            <div className="mcp-console-body">
              <pre className="mcp-code-container">
                {activeMcpTab === 'vscode' ? (
                  `{
  "mcpServers": {
    "linqra-gateway": {
      "type": "sse",
      "url": "https://localhost:7777/api/mcp/sse"
    }
  }
}`
                ) : activeMcpTab === 'cursor' ? (
                  `{
  "mcpServers": {
    "linqra-gateway": {
      "type": "sse",
      "url": "https://localhost:7777/api/mcp/sse"
    }
  }
}`
                ) : activeMcpTab === 'claude' ? (
                  `{
  "mcpServers": {
    "linqra-gateway": {
      "command": "node",
      "args": [
        "/Users/mehmetsen/IdeaProjects/Linqra/.roo/mcp-bridge.js"
      ]
    }
  }
}`
                ) : (
                  `{
  "mcpServers": [
    {
      "name": "linqra-gateway",
      "url": "https://localhost:7777/api/mcp/sse"
    }
  ]
}`
                )}
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* TECHNICAL DETAILS (Always Visible) */}
      <div className="developer-deep-dive-section">
        <h2>Technical & Compliance Validation</h2>

        <div className="dev-grid-layout">
          {/* COLUMN 1: SECURITY SPECS (User Liked This) */}
          <div className="dev-column">
            <h3>Security Specifications</h3>
            <div className="specs-table-container">
              <table className="specs-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Specification</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Encryption Algo</strong></td>
                    <td>AES-256-GCM (NIST Approved)</td>
                  </tr>
                  <tr>
                    <td><strong>Key Management</strong></td>
                    <td>Vault + Per-Tenant Rotary Keys</td>
                  </tr>
                  <tr>
                    <td><strong>Audit Integrity</strong></td>
                    <td>S3 Object Lock (WORM) Compliant</td>
                  </tr>
                  <tr>
                    <td><strong>Data Isolation</strong></td>
                    <td>Cryptographic & Logical (VPC)</td>
                  </tr>
                  <tr>
                    <td><strong>Transport Security</strong></td>
                    <td>TLS 1.3 (Mutual Auth Supported)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* COLUMN 2: COMPLIANCE MATRIX (New Table) */}
          <div className="dev-column">
            <h3>Compliance Coverage</h3>
            <div className="specs-table-container">
              <table className="specs-table">
                <thead>
                  <tr>
                    <th>Requirement</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>HIPAA (PHI)</strong></td>
                    <td><i className="fas fa-check-circle" style={{ color: '#10b981' }}></i> Ready</td>
                  </tr>
                  <tr>
                    <td><strong>GDPR / CCPA</strong></td>
                    <td><i className="fas fa-check-circle" style={{ color: '#10b981' }}></i> Ready</td>
                  </tr>
                  <tr>
                    <td><strong>SOC 2 Type II</strong></td>
                    <td><i className="fas fa-check-circle" style={{ color: '#10b981' }}></i> Ready</td>
                  </tr>
                  <tr>
                    <td><strong>GLBA (Finance)</strong></td>
                    <td><i className="fas fa-check-circle" style={{ color: '#10b981' }}></i> Ready</td>
                  </tr>
                  <tr>
                    <td><strong>Data Residency</strong></td>
                    <td><i className="fas fa-check-circle" style={{ color: '#10b981' }}></i> US/EU</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <ImageModal
        show={!!modalImageSrc}
        onHide={() => setModalImageSrc(null)}
        imageSrc={modalImageSrc}
      />
    </div>
  );
}

export default Home;